import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { checkForSatelliteAndInitialize } from '@nmx/utils/dist/utilities/frontend/Analytics/dtm_helper';

import Head from '../Head';
import Header from '../Header';
import Footer from '../Footer';
import Scripts from '../Scripts';
import SvgPreload from '../SvgPreload';

// Grab UTM codes from the url and store them in the session so they can be utilized later for tracking purposes
import '../../../assets/js/session/utm';

export const PageComponent = ({
  // ENV VARS
  env,
  // PAGE COMPONENT
  children,
  // HEAD PROPS
  headComponent,
  metaRobotsNoIndexNoFollow,
  siteTitleFirst,
  title,
  description,
  canonical,
  optimizelyProjectId,
  useReCaptcha,
  // HEADER PROPS
  hideHeader,
  headerIsOverlayed,
  headerLogoEyebrow,
  headerLogoEyebrowLarge,
  headerWithoutBorder,
  showInformationalBanner,
  skipNavAside,
  // FOOTER PROPS
  disclaimerFootnotes,
  customDisclaimer,
  isAmp,
  legalCopy,
  appendFooter,
  showExpandableToast,
  showStickyBanner,
  // SCRIPTS PROPS
  showChatBot,
  floodlightSendTo,
  showBingPixel,
  showFacebookPixel,
  showPinterestPixel,
  showGooglePlaces,
}) => {
  useEffect(() => {
    // get satellite on the page
    checkForSatelliteAndInitialize(1000, 10);
  }, []);

  return (
    <React.Fragment>
      {/* Head properties added to the head tag with Helmet */}
      <Head
        env={env}
        Container={headComponent}
        metaRobotsNoIndexNoFollow={metaRobotsNoIndexNoFollow}
        siteTitleFirst={siteTitleFirst}
        title={title}
        description={description}
        canonical={canonical}
        optimizelyProjectId={optimizelyProjectId}
        useReCaptcha={useReCaptcha}
      />

      {/* Header and nav for the page */}
      <Header
        env={env}
        hideHeader={hideHeader}
        headerIsOverlayed={headerIsOverlayed}
        headerLogoEyebrow={headerLogoEyebrow}
        headerLogoEyebrowLarge={headerLogoEyebrowLarge}
        headerWithoutBorder={headerWithoutBorder}
        showInformationalBanner={showInformationalBanner}
        skipNavAside={skipNavAside}
      />

      {/* Main document where page components are pased down to - everything specific to the page  */}
      <main
        id="main"
        role="main"
        className="nmx-base"
        style={{
          WebkitBoxSizing: 'border-box',
          boxSizing: 'border-box',
        }}>
        {children}
      </main>

      {/* Footer and properties added for the page */}
      <Footer
        env={env}
        disclaimerFootnotes={disclaimerFootnotes}
        customDisclaimer={customDisclaimer}
        isAmp={isAmp}
        legalCopy={legalCopy}
        appendFooter={appendFooter}
        showExpandableToast={showExpandableToast}
        showStickyBanner={showStickyBanner}
      />

      {/* Page scripts */}
      <Scripts
        env={env}
        showChatBot={showChatBot}
        floodlightSendTo={floodlightSendTo}
        showBingPixel={showBingPixel}
        showFacebookPixel={showFacebookPixel}
        showPinterestPixel={showPinterestPixel}
        showGooglePlaces={showGooglePlaces}
      />

      {/* Preloaded svgs */}
      <SvgPreload />
    </React.Fragment>
  );
};

PageComponent.propTypes = {
  /** Env vars used for varying components */
  env: PropTypes.shape({
    basePath: PropTypes.string.isRequired,
    nmxTemplateVersion: PropTypes.string.isRequired,
    adobeLaunchScript: PropTypes.string.isRequired,
    heapAppId: PropTypes.string.isRequired,
    newRelicApplicationId: PropTypes.string.isRequired,
    newRelicLicenseKey: PropTypes.string.isRequired,
    public: PropTypes.shape({
      googlePlacesApiKey: PropTypes.string,
      googleFloodlightsId: PropTypes.string,
      googleAdWordsId: PropTypes.string,
      googleAnalyticsTrackingId: PropTypes.string,
      recaptchaV3: PropTypes.string,
    }).isRequired,
    services: PropTypes.shape({
      loginPageUrl: PropTypes.string.isRequired,
      loginPageUrlBase: PropTypes.string.isRequired,
      userLogoutUrls: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,

  /** Component to render inside the page - everything other than header, footer, scripts, etc */
  children: PropTypes.any.isRequired,

  /** HEAD: container for head component. Replace with react-helmet or react-scripts helment if not wanting default <head /> */
  headComponent: PropTypes.any,
  /** HEAD: Do not index page with search engines */
  metaRobotsNoIndexNoFollow: PropTypes.bool,
  /** HEAD: Should title meta tag show site title first? */
  siteTitleFirst: PropTypes.bool,
  /** HEAD: Title to be placed in head title meta tag */
  title: PropTypes.string.isRequired,
  /** HEAD: Description to be placed in head description meta tag */
  description: PropTypes.string.isRequired,
  /** HEAD: canonical */
  canonical: PropTypes.string.isRequired,
  /** HEAD: Provide optimizely project id, if provided, will be passed to loaded optimzely script */
  optimizelyProjectId: PropTypes.string,
  /** HEAD: Does this page use recaptch? If true will place script on page */
  useReCaptcha: PropTypes.bool,

  /** HEADER: Will enable logo-only header and remove any primary navigation including login */
  hideHeader: PropTypes.bool,
  /** HEADER: Full primary nav with gradient overlay on top of body content. Coupled w/ hideHeader shows white logo overlayed on top of dark body */
  headerIsOverlayed: PropTypes.bool,
  /** HEADER: Will enable logo-only block header above body content */
  headerLogoEyebrow: PropTypes.bool,
  /** HEADER: Same as headerLogoEyebrow but larger */
  headerLogoEyebrowLarge: PropTypes.bool,
  /** HEADER: Will remove bottom header border logo-only or full nav */
  headerWithoutBorder: PropTypes.bool,
  /** HEADER: showInformationalBanner */
  showInformationalBanner: PropTypes.bool,
  /** HEADER: skipNavAside */
  skipNavAside: PropTypes.bool,

  /** FOOTER: Provide custom disclaimer footnotes to left of stock disclaimer */
  disclaimerFootnotes: PropTypes.any,
  /** FOOTER: Provide custom disclaimer to left of stock disclaimer */
  customDisclaimer: PropTypes.any,
  /** FOOTER: Is this an amp page? */
  isAmp: PropTypes.bool,
  /** FOOTER: TODO - STILL BEING USED? Use legal copy. */
  legalCopy: PropTypes.any,
  /** FOOTER: TODO - STILL BEING USED? Append additional content to footer. */
  appendFooter: PropTypes.any,
  /** FOOTER: Show expandable toad (ribbit) - currently covid message */
  showExpandableToast: PropTypes.bool,
  /** FOOTER: Show sticky banner - currently connect to FAFA */
  showStickyBanner: PropTypes.bool,

  /** SCRIPTS: Show chatbot */
  showChatBot: PropTypes.bool,
  /** SCRIPTS: Location to send to floodlight */
  floodlightSendTo: PropTypes.string,
  /** SCRIPTS: Show and add bing pixel script */
  showBingPixel: PropTypes.bool,
  /** SCRIPTS: Show and add facebook pixel script */
  showFacebookPixel: PropTypes.bool,
  /** SCRIPTS: Show and add pinterest pixel script */
  showPinterestPixel: PropTypes.bool,
  /** SCRIPTS: Show and add google places script */
  showGooglePlaces: PropTypes.bool,
};

PageComponent.defaultProps = {
  metaRobotsNoIndexNoFollow: false,
  siteTitleFirst: false,

  hideHeader: false,
  headerIsOverlayed: false,
  headerWithoutBorder: false,
  showInformationalBanner: false,
  skipNavAside: false,

  isAmp: false,
  showExpandableToast: false,
  showStickyBanner: false,

  showChatBot: false,
  showBingPixel: true,
  showFacebookPixel: true,
  showPinterestPixel: true,
  showGooglePlaces: false,
};

export default PageComponent;
